/**
 * MyOrders.js
 * 
 * Raider Depot — Order History & Status (Authenticated Users Only)
 * Route: /my-orders
 * 
 * Requires Firebase Email/Password auth (Tier 2 adult account).
 * Displays all orders for the linked token across all cycles,
 * with status tracking (submitted → packed → ready → picked-up).
 */

import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged, signOut, reauthenticateWithCredential, EmailAuthProvider, updatePassword } from "firebase/auth";
import NavBar from "../components/NavBar";

// ── Constants ───────────────────────────────────────────────────────
const ACCOUNT_API_URL = "/api/accounts";
const ORDERS_API_URL = "/api/orders";

// Status display config
const STATUS_MAP = {
  submitted: { label: "Order Submitted", color: "#3b82f6", icon: "📝" },
  packed: { label: "Bag Packed", color: "#f59e0b", icon: "📦" },
  ready: { label: "Ready for Pickup", color: "#22c55e", icon: "✅" },
  "picked-up": { label: "Picked Up", color: "#94a3b8", icon: "🏠" },
  cancelled: { label: "Cancelled", color: "#ef4444", icon: "❌" },
};

// ── Styles ──────────────────────────────────────────────────────────
const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f4f8",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "24px 16px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    padding: "24px 20px",
    maxWidth: "520px",
    width: "100%",
    marginBottom: "16px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  title: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#1a365d",
    margin: 0,
  },
  tokenBadge: {
    backgroundColor: "#ebf5ff",
    color: "#2563eb",
    padding: "4px 12px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "700",
    letterSpacing: "1px",
  },
  signOutBtn: {
    background: "none",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "6px 12px",
    fontSize: "12px",
    color: "#64748b",
    cursor: "pointer",
  },
  orderCard: {
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "12px",
  },
  orderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  cycleBadge: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#334155",
  },
  statusBadge: {
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },
  itemList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  itemRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
    fontSize: "13px",
    color: "#475569",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#94a3b8",
  },
  loginPrompt: {
    textAlign: "center",
    padding: "60px 20px",
  },
  nav: {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
    maxWidth: "520px",
    width: "100%",
  },
  navLink: {
    fontSize: "13px",
    color: "#3b82f6",
    textDecoration: "none",
  },
  loadingSpinner: {
    textAlign: "center",
    padding: "40px",
    color: "#64748b",
  },
};

// ── Component ───────────────────────────────────────────────────────
export default function MyOrders() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [tokenId, setTokenId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [catalogMap, setCatalogMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showChangePW, setShowChangePW] = useState(false);
  const [currentPW, setCurrentPW] = useState("");
  const [newPW, setNewPW] = useState("");
  const [confirmPW, setConfirmPW] = useState("");
  const [pwMessage, setPwMessage] = useState("");
  const [pwError, setPwError] = useState("");

  // ── Auth listener ─────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  // ── Load account + orders when authenticated ──────────────────────
  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        const idToken = await user.getIdToken();

        // Fetch catalog to build itemId → display name lookup
        try {
          const catalogRes = await fetch("/api/catalog");
          if (catalogRes.ok) {
            const catalogData = await catalogRes.json();
            const catItems = Array.isArray(catalogData) ? catalogData : catalogData.items || [];
            const map = {};
            catItems.forEach((c) => { map[c.itemId || c.id] = c.name; });
            setCatalogMap(map);
          }
        } catch (catErr) {
          console.warn("Could not load catalog for name resolution:", catErr);
        }

        // Fetch account to get linked tokenId
        const acctRes = await fetch(`${ACCOUNT_API_URL}/me`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });

        if (!acctRes.ok) {
          setError("Could not load account. Please try signing in again.");
          return;
        }

        const acctData = await acctRes.json();
        setTokenId(acctData.tokenId);

        // Fetch orders for this token
        if (acctData.tokenId) {
          const ordersRes = await fetch(
            `${ORDERS_API_URL}/history?tokenId=${acctData.tokenId}`,
            {
              headers: { Authorization: `Bearer ${idToken}` },
            }
          );

          if (ordersRes.ok) {
            const ordersData = await ordersRes.json();
            const orderList = Array.isArray(ordersData)
              ? ordersData
              : ordersData.orders || [];
            // Sort by cycle descending (most recent first)
            orderList.sort((a, b) => (b.cycleId || "").localeCompare(a.cycleId || ""));
            setOrders(orderList);
          }
        }
      } catch (err) {
        setError("Failed to load orders. Please try again.");
        console.error("MyOrders load error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const handleChangePassword = async () => {
    setPwMessage(""); setPwError("");
    if (!currentPW || !newPW || !confirmPW) { setPwError("All fields are required."); return; }
    if (newPW !== confirmPW) { setPwError("New passwords do not match."); return; }
    if (newPW.length < 6) { setPwError("New password must be at least 6 characters."); return; }
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPW);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPW);
      setPwMessage("Password updated successfully.");
      setShowChangePW(false); setCurrentPW(""); setNewPW(""); setConfirmPW("");
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setPwError("Current password is incorrect.");
      } else if (err.code === "auth/weak-password") {
        setPwError("New password is too weak. Use at least 6 characters.");
      } else {
        setPwError("Failed to update password. Please try again.");
      }
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    window.location.href = "/";
  };

  // ── Loading state ─────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div style={styles.container}>
        <NavBar title="My Orders" />
        <div style={styles.loadingSpinner}>Loading...</div>
      </div>
    );
  }

  // ── Not authenticated ─────────────────────────────────────────────
  if (!user) {
    return (
      <div style={styles.container}>
        <NavBar title="My Orders" />
        <div style={{ ...styles.card, ...styles.loginPrompt }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <h2 style={{ fontSize: "20px", color: "#1a365d", marginBottom: "8px" }}>
            Sign In Required
          </h2>
          <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "24px" }}>
            Sign in to your Raider Depot account to view your order history.
          </p>
          <a
            href="/login"
            style={{
              ...styles.navLink,
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            Sign In →
          </a>
        </div>
      </div>
    );
  }

  // ── Authenticated ─────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <NavBar title="My Orders" />
      {/* Navigation */}
      <div style={styles.nav}>
        <a href="/order" style={styles.navLink}>← Place Order</a>
        <span style={{ color: "#cbd5e1" }}>|</span>
        <a href="/my-orders" style={{ ...styles.navLink, fontWeight: "600" }}>
          My Orders
        </a>
      </div>

      {/* Header card */}
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>My Orders</h1>
            <p style={{ fontSize: "13px", color: "#94a3b8", margin: "4px 0 0 0" }}>
              {user.email}
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            {tokenId && <div style={styles.tokenBadge}>{tokenId}</div>}
            <button style={{ ...styles.signOutBtn, marginTop: "8px" }} onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={styles.loadingSpinner}>Loading your orders...</div>
      )}

      {/* Error */}
      {error && (
        <div style={styles.card}>
          <p style={{ color: "#ef4444", textAlign: "center" }}>{error}</p>
        </div>
      )}

      {/* Orders list */}
      {!loading && orders.length === 0 && (
        <div style={{ ...styles.card, ...styles.emptyState }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>📋</div>
          <p style={{ fontSize: "16px", color: "#475569", marginBottom: "8px" }}>
            No orders yet
          </p>
          <p style={{ fontSize: "13px", marginBottom: "20px" }}>
            When you place an order, it will appear here with status updates.
          </p>
          <a href="/order" style={styles.navLink}>
            Place your first order →
          </a>
        </div>
      )}

      {orders.map((order) => {
        const status = STATUS_MAP[order.status] || STATUS_MAP.submitted;
        return (
          <div key={order.orderId} style={styles.card}>
            <div style={styles.orderHeader}>
              <span style={styles.cycleBadge}>
                Cycle: {order.cycleId || "—"}
              </span>
              <span
                style={{
                  ...styles.statusBadge,
                  backgroundColor: `${status.color}15`,
                  color: status.color,
                }}
              >
                {status.icon} {status.label}
              </span>
            </div>

            {order.items && order.items.length > 0 && (
              <ul style={styles.itemList}>
                {order.items.map((item, idx) => (
                  <li key={idx} style={styles.itemRow}>
                    <span>{catalogMap[item.itemId] || item.name || item.itemId}</span>
                    <span style={{ fontWeight: "600" }}>×{item.qty}</span>
                  </li>
                ))}
              </ul>
            )}

            <div style={{ marginTop: "10px", fontSize: "11px", color: "#cbd5e1" }}>
              Submitted: {order.submittedAt
                ? new Date(order.submittedAt._seconds ? order.submittedAt._seconds * 1000 : order.submittedAt).toLocaleDateString()
                : "—"}
              {order.orderId && ` · ${order.orderId.slice(0, 8)}...`}
            </div>
          </div>
        );
      })}
      {/* Account Settings — Change Password */}
      <div style={{ ...styles.card, marginTop: "8px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#334155", margin: 0 }}>Account Settings</h3>
          <button
            onClick={() => { setShowChangePW(!showChangePW); setPwError(""); setPwMessage(""); }}
            style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "6px 14px", fontSize: "13px", color: "#475569", cursor: "pointer" }}
          >
            {showChangePW ? "Cancel" : "Change Password"}
          </button>
        </div>
        {showChangePW && (
          <div style={{ marginTop: "16px" }}>
            <input type="password" value={currentPW} onChange={(e) => setCurrentPW(e.target.value)} placeholder="Current password"
              style={{ width: "100%", padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", marginBottom: "8px", fontSize: "14px", boxSizing: "border-box" }} />
            <input type="password" value={newPW} onChange={(e) => setNewPW(e.target.value)} placeholder="New password"
              style={{ width: "100%", padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", marginBottom: "8px", fontSize: "14px", boxSizing: "border-box" }} />
            <input type="password" value={confirmPW} onChange={(e) => setConfirmPW(e.target.value)} placeholder="Confirm new password"
              style={{ width: "100%", padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", marginBottom: "12px", fontSize: "14px", boxSizing: "border-box" }} />
            <button onClick={handleChangePassword}
              style={{ width: "100%", padding: "10px", backgroundColor: "#2563eb", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>
              Update Password
            </button>
          </div>
        )}
        {pwMessage && <p style={{ color: "#16a34a", fontSize: "13px", marginTop: "8px" }}>{pwMessage}</p>}
        {pwError && <p style={{ color: "#ef4444", fontSize: "13px", marginTop: "8px" }}>{pwError}</p>}
      </div>
    </div>
  );
}
